import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import CreateProjectModal from '../CreateProjectModal/CreateProjectModal';
import { projectsApi } from '../../utils/projectsApi';
import styles from './ManageProjectsModal.module.scss';
import { ReactComponent as DragHandleIcon } from './assets/dragHandle.svg';
import { ReactComponent as EditIcon } from './assets/edit.svg';
import { ReactComponent as DeleteIcon } from './assets/delete.svg';
import { ReactComponent as AddIcon } from '../../assets/plus.svg';
import { Tooltip } from 'react-tooltip';

interface Project {
  id: number;
  name: string;
  case_ids?: number[];
  order?: number;
}

interface ManageProjectsModalProps {
  isVisible: boolean;
  onClose: () => void;
  projects: Project[];
  onProjectsChange: (projects: Project[]) => void;
  onCreateProject: () => void;
}

export default function ManageProjectsModal({
  isVisible,
  onClose,
  projects,
  onProjectsChange,
  onCreateProject,
}: ManageProjectsModalProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<Project[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Drag state
  const dragIndexRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Sync items when modal opens or projects prop changes
  useEffect(() => {
    if (isVisible) {
      setItems([...projects].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    }
  }, [isVisible, projects]);

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  const handleDragStart = (e: React.DragEvent<HTMLLIElement>, index: number) => {
    dragIndexRef.current = index;
    setDraggingIndex(index);

    const el = e.currentTarget;
    const clone = el.cloneNode(true) as HTMLElement;
    const bg = getComputedStyle(document.documentElement).getPropertyValue('--clr-gray100').trim() || '#f9fbff';
    clone.style.cssText = `
      width: ${el.offsetWidth}px;
      background: ${bg};
      position: absolute;
      top: -9999px;
      left: -9999px;
      box-sizing: border-box;
    `;
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    requestAnimationFrame(() => clone.remove());
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLLIElement>, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback(
    (dropIndex: number) => {
      const fromIndex = dragIndexRef.current;
      if (fromIndex === null || fromIndex === dropIndex) {
        dragIndexRef.current = null;
        setDragOverIndex(null);
        return;
      }

      const reordered = [...items];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(dropIndex, 0, moved);

      dragIndexRef.current = null;
      setDragOverIndex(null);

      const previousItems = [...items];
      setItems(reordered);

      // Persist new order
      setIsSavingOrder(true);
      Promise.all(
        reordered.map((project, i) =>
          projectsApi.update(project.id, { order: i + 1 }),
        ),
      )
        .then(() => {
          onProjectsChange(reordered.map((p, i) => ({ ...p, order: i + 1 })));
        })
        .catch(() => {
          setItems(previousItems);
          toast.error(t('Failed to save project order'));
        })
        .finally(() => setIsSavingOrder(false));
    },
    [items, onProjectsChange],
  );

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = useCallback(() => {
    const id = deleteConfirmId;
    if (!id) return;

    projectsApi.delete(id)
      .then(() => {
        const updated = items.filter((p) => p.id !== id);
        setItems(updated);
        onProjectsChange(updated);
        setDeleteConfirmId(null);
        if (updated.length === 0) onClose();
      })
      .catch(() => {
        toast.error(t('Failed to delete project'));
        setDeleteConfirmId(null);
      });
  }, [deleteConfirmId, items, onProjectsChange, onClose, t]);

  // ── Edit save ─────────────────────────────────────────────────────────────
  const handleEditSuccess = useCallback(
    (updatedProject: Project) => {
      const updated = items.map((p) =>
        p.id === updatedProject.id ? { ...p, ...updatedProject } : p,
      );
      setItems(updated);
      onProjectsChange(updated);
      setEditProject(null);
    },
    [items, onProjectsChange],
  );

  // ── Content ───────────────────────────────────────────────────────────────
  const content = (
    <>
      <ul className={styles.list} aria-label={t('Projects')}>
        {items.map((project, index) => (
          <li
            key={project.id}
            className={`${styles.row} ${draggingIndex === index ? styles.rowDragging : ''} ${dragOverIndex === index && draggingIndex !== index ? styles.rowDragOver : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={() => handleDrop(index)}
            onDragEnd={handleDragEnd}
            aria-label={project.name}
          >
            <span className={styles.dragHandle} aria-hidden="true">
              <DragHandleIcon />
            </span>

            <span className={styles.projectInfo}>
              <span className={styles.projectName}>{project.name}</span>
              <span className={styles.caseCount}>
                {t('projectCasesCount', { count: project.case_ids?.length ?? 0 })}
              </span>
            </span>

            <div className={styles.rowActions}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => setEditProject(project)}
                title={t('Edit project')}
                aria-label={`${t('Edit')} ${project.name}`}
                data-tooltip-id='edit-project'
                data-tooltip-content={t('Edit')}
              >
                <EditIcon />
              </button>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionBtnDelete}`}
                onClick={() => setDeleteConfirmId(project.id)}
                title={t('Delete project')}
                aria-label={`${t('Delete')} ${project.name}`}
                data-tooltip-id='delete-project'
                data-tooltip-content={t('Delete')}
              >
                <DeleteIcon />
              </button>
            </div>
          </li>
        ))}
      </ul>
      <Tooltip id='edit-project' place="bottom" style={{zIndex: 300}} />
      <Tooltip id='delete-project' place="bottom" style={{zIndex: 300}} />
    </>
  );

  const footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.createButton}
        onClick={onCreateProject}
      >
        <AddIcon aria-hidden="true" />
        {t('Create project')}
      </button>
    </div>
  );

  return (
    <>
      <Modal
        isVisible={isVisible && !deleteConfirmId && !editProject}
        title={t('Manage projects')}
        content={content}
        footer={footer}
        onClose={onClose}
        className={styles.modal}
      />

      {/* Delete confirmation */}
      <Modal
        isVisible={Boolean(deleteConfirmId)}
        title={t('Delete project')}
        className={styles.deleteModal}
        content={
          <p>
            {t(
              'The project will be deleted. Cases inside it will remain and can be found in "All cases".',
            )}
          </p>
        }
        footer={
          <>
            <button
              type="button"
              className="btn-reset"
              onClick={() => setDeleteConfirmId(null)}
            >
              {t('Cancel')}
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={handleDeleteConfirm}
            >
              {t('Delete project')}
            </button>
          </>
        }
        onClose={() => setDeleteConfirmId(null)}
      />

      {/* Edit project */}
      <CreateProjectModal
        isVisible={Boolean(editProject)}
        initialProject={editProject}
        onClose={() => setEditProject(null)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}
